import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, Warning, Info, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';

export type DialogType = 'confirm' | 'warning' | 'danger' | 'input' | 'info';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string | React.ReactNode;
  type?: DialogType;
  confirmText?: string;
  cancelText?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputRequired?: boolean;
  inputMultiline?: boolean;
  inputRows?: number;
  defaultInputValue?: string;
  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  type = 'confirm',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  inputLabel,
  inputPlaceholder,
  inputRequired = false,
  inputMultiline = false,
  inputRows = 4,
  defaultInputValue = '',
  onConfirm,
  onCancel,
}) => {
  const [inputValue, setInputValue] = React.useState(defaultInputValue);
  const [inputError, setInputError] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setInputValue(defaultInputValue);
      setInputError(false);
    }
  }, [open, defaultInputValue]);

  const handleConfirm = () => {
    if (type === 'input') {
      if (inputRequired && !inputValue.trim()) {
        setInputError(true);
        return;
      }
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <Warning sx={{ color: 'warning.main', fontSize: 48, mb: 2 }} />;
      case 'danger':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 48, mb: 2 }} />;
      case 'info':
        return <Info sx={{ color: 'info.main', fontSize: 48, mb: 2 }} />;
      default:
        return null;
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 1 }}>
          {getIcon()}
        </Box>
        {typeof message === 'string' ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}

        {type === 'input' && (
          <TextField
            autoFocus
            margin="dense"
            label={inputLabel}
            placeholder={inputPlaceholder}
            fullWidth
            variant="outlined"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setInputError(false);
            }}
            error={inputError}
            helperText={inputError ? 'This field is required' : ''}
            required={inputRequired}
            multiline={inputMultiline}
            rows={inputMultiline ? inputRows : undefined}
            sx={{ mt: 2 }}
          />
        )}

        {type === 'danger' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This action cannot be undone!
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onCancel} color="inherit">
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color={getConfirmButtonColor()}
          variant="contained"
          autoFocus={type !== 'input'}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;